import { Button } from "@/components/flexkit/Button";
import { Combobox } from "@/components/flexkit/combobox";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Label } from "@/components/podkit/forms/Label";
import { DialogClose } from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useGetWebhookSecret, useUpdateWebhook, type PlainWebhook } from "@/queries/webhook-queries";
import {
    useListRunners,
    useListSCMOrganizationsAcrossRunners,
    useSearchRepositoriesAcrossRunners,
} from "@/queries/runner-queries";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { SearchMode, RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";

type WebhookDetailsConfigurationStepProps = {
    webhook: PlainWebhook;
    onClose: () => void;
};

type SCMRepository = {
    name: string;
    url: string;
};

const getWebhookSettingsUrl = (provider: WebhookProvider, type: WebhookType, scope: string): string => {
    if (!scope) return "";

    if (provider === WebhookProvider.GITHUB) {
        if (type === WebhookType.SCM_ORGANIZATION) {
            return `https://github.com/organizations/${scope}/settings/hooks/new`;
        }
        return `https://github.com/${scope}/settings/hooks/new`;
    }

    if (provider === WebhookProvider.GITLAB) {
        if (type === WebhookType.SCM_ORGANIZATION) {
            return `https://gitlab.com/groups/${scope}/-/hooks`;
        }
        return `https://gitlab.com/${scope}/-/hooks`;
    }

    return "";
};

// Extract owner/repo from URL like "https://github.com/owner/repo"
const extractRepoPath = (url: string): string => {
    try {
        const urlObj = new URL(url);
        return urlObj.pathname.slice(1); // Remove leading slash
    } catch {
        return url;
    }
};

export const WebhookDetailsConfigurationStep: FC<WebhookDetailsConfigurationStepProps> = ({ webhook, onClose }) => {
    const { toast } = useToast();
    const { data: secretToken, isLoading: isLoadingSecret } = useGetWebhookSecret(webhook.id);
    const { mutateAsync: updateWebhook, isPending: isUpdating } = useUpdateWebhook();
    const { runners: allRunners } = useListRunners({});
    const runners = useMemo(() => allRunners.filter((r) => r.status?.phase === RunnerPhase.ACTIVE), [allRunners]);

    const provider = webhook.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const webhookType = webhook.spec?.type ?? WebhookType.UNSPECIFIED;
    const payloadUrl = webhook.url || "";

    let scmHost: string;
    let providerName: string;
    let isOrgType: boolean;
    let scopeLabel: string;
    let comboboxPlaceholder: string;
    switch (provider) {
        case WebhookProvider.GITHUB:
            scmHost = "github.com";
            providerName = "GitHub";
            break;
        case WebhookProvider.GITLAB:
            scmHost = "gitlab.com";
            providerName = "GitLab";
            break;
        default:
            scmHost = "";
            providerName = "";
            break;
    }
    switch (webhookType) {
        case WebhookType.SCM_ORGANIZATION:
            isOrgType = true;
            scopeLabel = "Organization";
            comboboxPlaceholder = "Select an organization";
            break;
        case WebhookType.SCM_REPOSITORY:
            isOrgType = false;
            scopeLabel = "Repository";
            comboboxPlaceholder = "Select a repository";
            break;
        default:
            isOrgType = false;
            scopeLabel = "";
            comboboxPlaceholder = "";
            break;
    }

    const [selectedScope, setSelectedScope] = useState<string>(webhook.spec?.scope || "");
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue] = useDebounce(searchValue, 250, { trailing: true });

    // Get runner IDs for querying across all runners
    const runnerIds = useMemo(() => runners.map((r) => r.runnerId), [runners]);

    // Fetch organizations from all runners
    const {
        organizations,
        isLoading: isLoadingOrgs,
        isFetching: isFetchingOrgs,
    } = useListSCMOrganizationsAcrossRunners({
        runnerIds,
        scmHost,
        enabled: isOrgType && runnerIds.length > 0 && !!scmHost,
    });

    // Fetch repositories from all runners with deduplication
    const { repositories: repoResults, isLoading: isLoadingRepos } = useSearchRepositoriesAcrossRunners({
        runnerIds,
        query: debouncedSearchValue,
        scmHost,
        searchMode: SearchMode.KEYWORD,
        enabled: !isOrgType && runnerIds.length > 0 && !!scmHost,
        pageSize: 50,
    });

    const repositories = useMemo(
        (): SCMRepository[] =>
            repoResults.map((repo) => ({
                name: repo.name,
                url: repo.url,
            })),
        [repoResults],
    );

    const settingsUrl = getWebhookSettingsUrl(provider, webhookType, selectedScope);

    const handleOpenSettings = () => {
        if (settingsUrl) {
            window.open(settingsUrl, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <div className="-m-1 flex flex-col gap-4 p-1">
            <div className="flex flex-col gap-2">
                <Label htmlFor="webhook-scope" className="text-sm font-medium">
                    {scopeLabel}
                </Label>
                <div className="flex items-center gap-1">
                    <Combobox
                        value={selectedScope}
                        onValueChange={setSelectedScope}
                        loading={isOrgType ? isLoadingOrgs : isLoadingRepos && !debouncedSearchValue}
                        className="flex-1"
                    >
                        <Combobox.Value>
                            {(value) => (
                                <Combobox.ValueLabel className="max-w-40 truncate">
                                    {value || comboboxPlaceholder}
                                </Combobox.ValueLabel>
                            )}
                        </Combobox.Value>
                        {isOrgType ? (
                            <Combobox.Popover sameWidth={false} className="min-w-[--popover-anchor-width] max-w-96">
                                <Combobox.SearchBox loading={isFetchingOrgs} />
                                {organizations && organizations.length > 0 ? (
                                    <Combobox.List
                                        items={organizations}
                                        searchKeys={["name"]}
                                        noMatchesComponent={<Combobox.Empty>No organizations found</Combobox.Empty>}
                                    >
                                        {(org) => (
                                            <Combobox.ListItem key={org.name} value={org.name} title={org.name} />
                                        )}
                                    </Combobox.List>
                                ) : (
                                    <Combobox.Empty>No organizations found</Combobox.Empty>
                                )}
                            </Combobox.Popover>
                        ) : (
                            <Combobox.Popover sameWidth={false} className="max-w-96">
                                <Combobox.SearchBox onValueChanged={setSearchValue} loading={isLoadingRepos} />
                                {repositories && repositories.length > 0 ? (
                                    <Combobox.List
                                        items={repositories}
                                        searchKeys={[]}
                                        disableFiltering
                                        noMatchesComponent={<Combobox.Empty>No repositories found</Combobox.Empty>}
                                    >
                                        {(repo) => (
                                            <Combobox.ListItem
                                                key={repo.url}
                                                value={extractRepoPath(repo.url)}
                                                title={extractRepoPath(repo.url)}
                                            />
                                        )}
                                    </Combobox.List>
                                ) : (
                                    <Combobox.Empty>No repositories found</Combobox.Empty>
                                )}
                            </Combobox.Popover>
                        )}
                    </Combobox>
                    <Button
                        variant="ghost"
                        onClick={handleOpenSettings}
                        disabled={!settingsUrl}
                        data-testid="open-webhook-settings"
                        data-tracking-id="open-webhook-settings"
                        className="gap-0 px-3 text-sm"
                    >
                        Open&nbsp;{providerName}&nbsp;webhook settings
                    </Button>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor="payload-url" className="text-sm font-medium">
                    Payload URL
                </Label>
                <CopyableInput
                    id="payload-url"
                    value={payloadUrl}
                    data-testid="payload-url-input"
                    data-tracking-id="copy-payload-url-webhook-setup"
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor="secret-token" className="text-sm font-medium">
                    Secret Token
                </Label>
                <CopyableInput
                    id="secret-token"
                    type="password"
                    value={secretToken || ""}
                    disabled={isLoadingSecret}
                    data-testid="secret-token-input"
                    data-tracking-id="copy-secret-token-webhook-setup"
                />
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <DialogClose asChild>
                    <Button variant="outline" type="button" data-testid="webhook-cancel" data-tracking-id="cancel">
                        Do later
                    </Button>
                </DialogClose>
                <Button
                    variant="primary"
                    type="button"
                    data-testid="webhook-done"
                    data-tracking-id="webhook-done"
                    disabled={!selectedScope || isUpdating}
                    loading={isUpdating}
                    onClick={async () => {
                        if (!selectedScope) return;
                        try {
                            await updateWebhook({ webhookId: webhook.id, scope: selectedScope });
                            toast({
                                title: "Webhook configured",
                                description: "You can now create an automation to use it.",
                            });
                            onClose();
                        } catch {
                            toast({
                                title: "Failed to save scope",
                                description: "Please try again.",
                            });
                        }
                    }}
                >
                    Done
                </Button>
            </div>
        </div>
    );
};
